import React from 'react'

interface FormattedTextProps {
  text: string
  style?: React.CSSProperties
}

export const FormattedText: React.FC<FormattedTextProps> = ({ text, style }) => {
  const formatText = (content: string) => {
    // Split by lines to handle line breaks
    const lines = content.split('\n')
    
    return lines.map((line, lineIndex) => {
      // Check if line is a bullet point
      const isBullet = line.startsWith('• ')
      const isNumbered = /^\d+\.\s/.test(line)
      
      // Process the line content (remove bullet/number prefix if present)
      let processedLine = line
      if (isBullet) {
        processedLine = line.substring(2)
      } else if (isNumbered) {
        processedLine = line.replace(/^\d+\.\s/, '')
      }
      
      // Replace markdown-style formatting
      const parts: (string | React.ReactElement)[] = []
      let lastIndex = 0
      
      // Combined regex to match bold (**text**) and italic (*text*)
      const regex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g
      let match
      
      while ((match = regex.exec(processedLine)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(processedLine.substring(lastIndex, match.index))
        }
        
        // Add formatted text
        if (match[1]) {
          // Bold text
          parts.push(
            <strong key={`bold-${lineIndex}-${match.index}`} style={{ fontWeight: 600 }}>
              {match[2]}
            </strong>
          )
        } else if (match[3]) {
          // Italic text
          parts.push(
            <em key={`italic-${lineIndex}-${match.index}`} style={{ fontStyle: 'italic' }}>
              {match[4]}
            </em>
          )
        }
        
        lastIndex = regex.lastIndex
      }
      
      // Add remaining text
      if (lastIndex < processedLine.length) {
        parts.push(processedLine.substring(lastIndex))
      }
      
      // If no formatting was found, just use the processed line
      if (parts.length === 0) {
        parts.push(processedLine)
      }
      
      // Wrap in appropriate element based on line type
      if (isBullet) {
        return (
          <div key={lineIndex} style={{ display: 'flex', marginBottom: '4px' }}>
            <span style={{ marginRight: '8px' }}>•</span>
            <span style={{ flex: 1 }}>{parts}</span>
          </div>
        )
      } else if (isNumbered) {
        const number = line.match(/^(\d+)\./)?.[1]
        return (
          <div key={lineIndex} style={{ display: 'flex', marginBottom: '4px' }}>
            <span style={{ marginRight: '8px' }}>{number}.</span>
            <span style={{ flex: 1 }}>{parts}</span>
          </div>
        )
      } else if (line.trim() === '') {
        return <br key={lineIndex} />
      } else {
        return (
          <div key={lineIndex} style={{ marginBottom: '4px' }}>
            {parts}
          </div>
        )
      }
    })
  }

  return (
    <div style={{ ...style, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {formatText(text)}
    </div>
  )
}